import { motion } from "framer-motion";
import { Activity, Radio, Pause, Play, AlertCircle, Zap, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ARCHIVE_THRESHOLD } from "./streamModel";

const SPIN = { rotate: 360 };
const SPIN_LOOP = { duration: 1, repeat: Infinity, ease: "linear" };
const DROP_IN = { opacity: 0, y: -10 };
const SETTLED = { opacity: 1, y: 0 };

const StatusPill = ({ isPaused, needsAcknowledgment }) => {
  if (isPaused) return <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/30"><Pause className="h-3 w-3 text-amber-400" /><span className="text-xs text-amber-400 font-medium">Paused</span></div>;
  if (needsAcknowledgment) return <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-ism-teal/10 border border-ism-teal/30"><AlertCircle className="h-3 w-3 text-ism-teal" /><span className="text-xs text-ism-teal font-medium">Waiting</span></div>;
  return <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-ism-teal/10 border border-ism-teal/30"><Radio className="h-3 w-3 text-ism-teal" /><span className="text-xs text-ism-teal font-medium">Live</span></div>;
};

const Subtitle = ({ isGenerating, isPaused, needsAcknowledgment, unacknowledgedCount, reviewedCount }) => {
  if (isGenerating) return (
    <span className="flex items-center gap-2">
      <motion.span animate={SPIN} transition={SPIN_LOOP}><Zap className="h-3 w-3 text-ism-teal" /></motion.span>
      Generating insights...
    </span>
  );
  if (isPaused) return "Paused - Click play to resume";
  if (needsAcknowledgment) return `${unacknowledgedCount} items need review`;
  if (reviewedCount >= ARCHIVE_THRESHOLD - 5) return <span className="text-amber-400">{reviewedCount}/{ARCHIVE_THRESHOLD} reviewed - archive soon</span>;
  return "Active - Running continuously";
};

const TAB_ACTIVE = "bg-ism-teal/20 text-ism-teal border border-ism-teal/30";
const TAB_IDLE = "text-gray-500 hover:text-ism-teal hover:bg-ism-teal/10";

const FilterTab = ({ id, active, onSelect, activeClass = TAB_ACTIVE, idleClass = TAB_IDLE, children }) => (
  <Button onClick={() => onSelect(id)} variant="ghost" size="sm" className={`rounded-full px-3 py-1 text-xs ${active ? activeClass : idleClass}`} data-testid={`filter-${id}`}>
    {children}
  </Button>
);

export const StreamHeader = ({ feed, isPaused, onPauseToggle, activeFilter, setActiveFilter, counts }) => (
  <div className="mb-6 relative z-10">
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-4 min-w-0">
        <div className="p-3 rounded-xl bg-gradient-to-br from-ism-teal/20 to-ism-teal/10 border border-ism-teal/20 flex-shrink-0">
          <Activity className="h-6 w-6 text-ism-teal" />
        </div>
        <div className="min-w-0">
          <h2 className="text-xl text-white flex items-center flex-wrap gap-x-3 gap-y-1 tracking-tight">
            <span className="text-ism-teal">Autonomous Stream</span>
            <StatusPill isPaused={isPaused} needsAcknowledgment={feed.needsAcknowledgment} />
          </h2>
          <p className="text-sm text-gray-500 mt-1"><Subtitle isPaused={isPaused} {...feed} /></p>
        </div>
      </div>
      <Button onClick={onPauseToggle} variant="ghost" size="sm" data-testid="stream-pause-btn"
        className={`rounded-full p-2 flex-shrink-0 ${isPaused ? "bg-ism-teal/10 hover:bg-ism-teal/20 text-ism-teal border border-ism-teal/30" : "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30"}`}>
        {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
      </Button>
    </div>

    {feed.needsAcknowledgment && (
      <motion.div initial={DROP_IN} animate={SETTLED} className="mt-4 p-3 rounded-lg bg-ism-teal/10 border border-ism-teal/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-ism-teal" />
          <div>
            <p className="text-sm text-gray-300">Stream paused - {feed.unacknowledgedCount} unreviewed insights</p>
            <p className="text-xs text-gray-400/70">Review or acknowledge to continue receiving new insights</p>
          </div>
        </div>
        <Button onClick={feed.acknowledgeAll} size="sm" className="bg-ism-teal/20 hover:bg-ism-teal/30 text-ism-teal border border-ism-teal/40" data-testid="acknowledge-stream-btn">
          Acknowledge All
        </Button>
      </motion.div>
    )}

    <div className="flex flex-wrap gap-2 mt-4">
      <FilterTab id="all" active={activeFilter === "all"} onSelect={setActiveFilter} activeClass="bg-white/10 text-white border border-white/20" idleClass="text-gray-500 hover:text-white hover:bg-white/5">All ({counts.all})</FilterTab>
      <FilterTab id="news" active={activeFilter === "news"} onSelect={setActiveFilter}>News ({counts.news})</FilterTab>
      <FilterTab id="research" active={activeFilter === "research"} onSelect={setActiveFilter}>Research ({counts.research})</FilterTab>
      <FilterTab id="leads" active={activeFilter === "leads"} onSelect={setActiveFilter}>Leads ({counts.leads})</FilterTab>
      <FilterTab id="actions" active={activeFilter === "actions"} onSelect={setActiveFilter}>Strategy ({counts.actions})</FilterTab>
      <FilterTab id="archive" active={activeFilter === "archive"} onSelect={setActiveFilter} activeClass="bg-gray-600/30 text-gray-300 border border-gray-500/30" idleClass="text-gray-500 hover:text-gray-300 hover:bg-gray-600/20">
        <Archive className="h-3 w-3 mr-1" />Archive ({feed.archiveStats.totalArchived})
      </FilterTab>
    </div>
  </div>
);
